import React, { useCallback, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import CustomDrawer from "../UniversalComponents/CustomDrawer";
import MotionSearchBox from "../UniversalComponents/MotionSearchBox";
import { ReactComponent as CrossIcon } from "../../assets/images/addTaskCrosIcn.svg";
import { ReactComponent as NotificationIcon } from "../../assets/images/icons/NotificationIconBlue.svg";
import useRequireAuth from "../../utils/useRequireAuth";
import { usePollingWithReset } from "../UniversalComponents/usePollingWithReset";
import {
  useFetchNotificationDataQuery,
  useMarkSingleNotificationAsReadMutation,
  useMarkAllNotificationAsReadMutation,
} from "./GetAllNotification";
import HighlightedParagraph from "../UniversalComponents/HighlightedParagraph";
import { ReactComponent as Notifications } from "../../assets/images/icons/NotificationsIcon.svg";

const IST_TZ = "Asia/Kolkata";

// ---------- Time helpers (IST-safe, no DST) ----------
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const toIST = (d) => new Date(d.getTime() + IST_OFFSET_MS);
const fromIST = (d) => new Date(d.getTime() - IST_OFFSET_MS);

const startOfISTDay = (d) => {
  const ist = toIST(d);
  const startIstUTC = Date.UTC(ist.getUTCFullYear(), ist.getUTCMonth(), ist.getUTCDate());
  return fromIST(new Date(startIstUTC));
};
const addDays = (d, days) => new Date(d.getTime() + days * 24 * 60 * 60 * 1000);
const startOfISTWeekMon = (d) => {
  // Monday = 0
  const ist = toIST(d);
  const dowSun0 = ist.getUTCDay(); // 0..6 (Sun..Sat)
  const dowMon0 = (dowSun0 + 6) % 7; // 0..6 (Mon..Sun)
  const startDay = addDays(new Date(Date.UTC(ist.getUTCFullYear(), ist.getUTCMonth(), ist.getUTCDate())), -dowMon0);
  return fromIST(startDay);
};
const startOfISTMonth = (d) => {
  const ist = toIST(d);
  const firstIstUTC = Date.UTC(ist.getUTCFullYear(), ist.getUTCMonth(), 1);
  return fromIST(new Date(firstIstUTC));
};
const startOfISTYear = (d) => {
  const ist = toIST(d);
  const firstIstUTC = Date.UTC(ist.getUTCFullYear(), 0, 1);
  return fromIST(new Date(firstIstUTC));
};

const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString("en-IN", { timeZone: IST_TZ, hour: "2-digit", minute: "2-digit" });

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", { timeZone: IST_TZ, day: "2-digit", month: "short", year: "numeric" });

const Notification = ({ open, onClose }) => {
  const user = useSelector((s) => s.user);
  const token = useRequireAuth();
  const stored = typeof window !== "undefined" ? localStorage.getItem("company_data") : null;
  const company = stored ? JSON.parse(stored) : null;

  const { data, error, isLoading, refetch } = useFetchNotificationDataQuery({
    user_id: user?.user_id,
    company_id: company?.company_id,
    token: token?.token,
  });

  // --- Polling like before ---
  const freshPollingCounter = useRef(0);
  const refetchPollingData = useCallback(() => {
    freshPollingCounter.current += 1;
    refetch && refetch();
  }, [refetch]);
  usePollingWithReset(refetchPollingData, [data], 10000, true, "Notification");

  const [markSingleNotificationAsRead, { isLoading: singleBusy }] = useMarkSingleNotificationAsReadMutation();
  const [markAllNotificationAsRead, { isLoading: allBusy }] = useMarkAllNotificationAsReadMutation();

  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState("All"); // 'All' | 'Unread' | 'Read'
  const nowRef = useRef(new Date());

  // raw list from API
  const rawRows = useMemo(() => (Array.isArray(data?.data) ? data.data : []), [data]);

  // counts
  const unreadCount = useMemo(() => rawRows.reduce((acc, n) => acc + (n.is_read === 0 ? 1 : 0), 0), [rawRows]);
  const totalCount = rawRows.length;
  const readCount = totalCount - unreadCount;

  // sort + tab filter + search
  const items = useMemo(() => {
    const sorted = [...rawRows].sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));
    const tabFiltered = sorted.filter((n) =>
      activeTab === "All" ? true : activeTab === "Unread" ? n.is_read === 0 : n.is_read === 1
    );
    const q = searchText.trim().toLowerCase();
    if (!q) return tabFiltered;
    return tabFiltered.filter((n) => `${n.title} ${n.message}`.toLowerCase().includes(q));
  }, [rawRows, searchText, activeTab]);

  // ---------- Grouping logic ----------
  const groups = useMemo(() => {
    const now = nowRef.current || new Date();

    const todayStart = startOfISTDay(now);
    const yesterdayStart = addDays(todayStart, -1);

    const thisWeekStart = startOfISTWeekMon(now);
    const lastWeekStart = addDays(thisWeekStart, -7);

    const thisMonthStart = startOfISTMonth(now);
    const lastMonthStart = startOfISTMonth(addDays(thisMonthStart, -1)); // go to previous month then start
    const lastMonthStartNorm = startOfISTMonth(lastMonthStart);

    const thisYearStart = startOfISTYear(now);
    const lastYearStart = startOfISTYear(addDays(thisYearStart, -1));

    const labelFor = (iso) => {
      const d = new Date(iso);
      if (d >= todayStart) return "Today";
      if (d >= yesterdayStart) return "Yesterday";
      if (d >= thisWeekStart) return "This Week";
      if (d >= lastWeekStart) return "Last Week";
      if (d >= thisMonthStart) return "This Month";
      if (d >= lastMonthStartNorm) return "Last Month";
      if (d >= thisYearStart) return "Earlier This Year";
      if (d >= lastYearStart) return "Last Year";
      return "Older";
    };

    const buckets = new Map();
    for (const n of items) {
      const k = labelFor(n.created_at);
      if (!buckets.has(k)) buckets.set(k, []);
      buckets.get(k).push(n);
    }

    // Display order
    const order = [
      "Today",
      "Yesterday",
      "This Week",
      "Last Week",
      "This Month",
      "Last Month",
      "Earlier This Year",
      "Last Year",
      "Older",
    ];

    // Build array of {label, rows}
    return order.map((label) => ({ label, rows: buckets.get(label) || [] })).filter((g) => g.rows.length > 0);
  }, [items]);

  const markOneRead = useCallback(
    async (id) => {
      try {
        await markSingleNotificationAsRead({ payload: { notificationId: id } }).unwrap?.();
      } catch (e) {
        console.error("markSingleNotificationAsRead failed", e);
      } finally {
        refetch?.();
      }
    },
    [markSingleNotificationAsRead, refetch]
  );

  const markAllRead = useCallback(async () => {
    try {
      await markAllNotificationAsRead({}).unwrap?.();
    } catch (e) {
      console.error("markAllNotificationAsRead failed", e);
    } finally {
      refetch?.();
    }
  }, [markAllNotificationAsRead, refetch]);

  const onOpenTarget = useCallback(
    async (href, id) => {
      await markOneRead(id);
      if (href && typeof window !== "undefined") window.location.href = href;
    },
    [markOneRead]
  );

  if (!open) return null;

  if (isLoading) {
    return (
      <CustomDrawer open={open} onClose={onClose}>
        <div className="p-4">Loading notifications…</div>
      </CustomDrawer>
    );
  }

  if (error) {
    return (
      <CustomDrawer open={open} onClose={onClose}>
        <div className="p-4 text-red-600">Failed to load notifications.</div>
      </CustomDrawer>
    );
  }

  return (
    <CustomDrawer open={open} onClose={onClose}>
      <div className="px-1 w-full h-full">
        <div className="pr-2 h-full flex flex-col">
          {/* Header */}
          <div className="flex justify-between py-2 px-3 border-b border-BECBD3 gap-2">
            <div className="flex flex-col grow min-w-0">
              <div className="flex flex-row items-center gap-2 ">
                <div className="relative inline-block">
                  <span className="text-primary">
                    <Notifications className="fill-current" />
                  </span>
                  {unreadCount > 0 && (
                    <span className="absolute text-xs flex items-center justify-center text-white min-w-[1.5rem] h-[1.5rem] px-1 bg-red-500 border-2 border-white rounded-full -top-3 start-1.5">
                      {unreadCount}
                    </span>
                  )}
                </div>
                <h3 className="text-lg text-[#1D4469] font-medium truncate">Notifications</h3>
              </div>
              <p className="text-[#3c3c3c99] text-sm truncate">Stay updated with your latest notifications</p>
            </div>
            <div className="flex items-center gap-3">
              <MotionSearchBox
                isSearchActive={isSearchActive}
                setIsSearchActive={setIsSearchActive}
                searchText={searchText}
                setSearchText={setSearchText}
                placeholder={"Search title or message…"}
              />
              <button aria-label="Close notifications">
                <CrossIcon onClick={onClose} />
              </button>
            </div>
          </div>

          {/* Tabs + Mark all */}
          <div className="flex items-center justify-between border p-2 mx-4 my-2 text-[#848687] border-[#D2DAE1] rounded-md bg-[#F1F6F9]">
            <div className="flex gap-1 text-sm">
              {[
                { k: "All", label: `All (${totalCount})` },
                { k: "Unread", label: `Unread (${unreadCount})` },
                { k: "Read", label: `Read (${readCount})` },
              ].map((t) => (
                <button
                  key={t.k}
                  onClick={() => setActiveTab(t.k)}
                  className={`px-1 py-1 rounded-md ${
                    activeTab === t.k ? "bg-white text-[#1D4469] shadow-sm" : "hover:bg-white/60"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <button
              onClick={markAllRead}
              disabled={allBusy}
              className="flex items-center hover:underline disabled:opacity-60 text-sm  hover:bg-white hover:text-[#1D4469] hover:shadow-sm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
                <path
                  d="M12.6159 7.20761C12.3449 6.93351 11.9032 6.93006 11.6279 7.1999L6.46364 12.3039L4.21869 9.97984C3.95094 9.70077 3.50767 9.69161 3.22863 9.95937C2.94956 10.2271 2.9404 10.6704 3.20816 10.9494C3.20937 10.9507 3.21055 10.9519 3.21177 10.9532L5.949 13.7898C6.07999 13.9235 6.25903 13.9991 6.44617 13.9999H6.45387C6.63746 13.9993 6.8135 13.9266 6.94404 13.7975L12.6096 8.19564C12.8833 7.92419 12.8861 7.48255 12.6159 7.20761Z"
                  fill="#888888"
                />
                <path
                  d="M10.1503 13.7899C10.2809 13.9232 10.4594 13.9988 10.646 14H10.6538C10.8373 13.9994 11.0134 13.9267 11.1439 13.7976L16.8095 8.19575C17.0663 7.90658 17.0401 7.464 16.751 7.2072C16.4888 6.97438 16.0949 6.97132 15.8292 7.20001L10.667 12.304L10.4829 12.1128C10.2151 11.8338 9.77187 11.8246 9.4928 12.0923C9.21374 12.3601 9.20455 12.8033 9.4723 13.0824C9.47351 13.0837 9.47473 13.0849 9.47594 13.0862L10.1503 13.7899Z"
                  fill="#888888"
                />
              </svg>
              <span>{allBusy ? "Marking…" : "Mark all as read"}</span>
            </button>
          </div>

          {/* Grouped list */}
          <div className="flex-1 overflow-y-auto px-2">
            {groups.length === 0 ? (
              <div className="text-sm text-[#6B7280] px-3 py-6">No notifications.</div>
            ) : (
              <div className="rounded-md">
                {groups.map((g) => (
                  <section key={g.label} className="mb-4">
                    <h4 className="px-3 pt-3 pb-2 text-xs font-semibold uppercase tracking-wide text-[#778FA5]">
                      {g.label}
                    </h4>
                    <ul role="list" aria-label={`${g.label} notifications`} className="rounded-md">
                      {g.rows.map((n) => (
                        <li
                          key={n.id}
                          className="w-full flex items-start justify-between border-b border-[#E6EDF2] px-3 py-3 hover:bg-[#F8FBFD] focus-within:bg-[#F8FBFD]"
                        >
                          <button
                            onClick={() => onOpenTarget(n.target_url, n.id)}
                            className="flex gap-3 items-start text-left outline-none focus-visible:ring-2 focus-visible:ring-[#1D4469]/30 rounded-md flex-1"
                          >
                            <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#E9F2F9] text-primary">
                              <span className="text-primary">
                                <Notifications className="fill-current" />
                              </span>
                              {n.is_read === 0 && (
                                <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-red-500 border-2 border-white" />
                              )}
                            </span>
                            <span className="min-w-0">
                              <span className="block text-[#1D4469] text-sm font-medium truncate cursor-text">
                                <HighlightedParagraph paragraph={n.title} searchTerm={searchText} />
                              </span>
                              <span className="block text-[#3c3c3c] text-sm/5 break-words  cursor-text">
                                <HighlightedParagraph paragraph={n.message} searchTerm={searchText} />
                              </span>
                              <span className="mt-1 block text-[11px] text-[#778FA5]">
                                {fmtDate(n.created_at)} • {fmtTime(n.created_at)}
                              </span>
                            </span>
                          </button>
                          {n.is_read === 0 && (
                            <button
                              onClick={() => markOneRead(n.id)}
                              disabled={singleBusy}
                              className="text-[11px] text-[#1D4469] hover:underline ml-3 shrink-0 disabled:opacity-60"
                              aria-label="Mark as read"
                            >
                              Mark read
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </CustomDrawer>
  );
};

export default Notification;
